/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
import { Arc3d, Sphere, Matrix3d, Point2d, Point3d, Transform, XAndY, XYAndZ} from "@itwin/core-geometry";
import { Cluster, DecorateContext, Decorator, GraphicBranch, Marker, MarkerSet, readGltfGraphics, RenderGraphic, ViewManager} from "@itwin/core-frontend";
import { IModelApp, IModelConnection, ScreenViewport} from "@itwin/core-frontend";
import { BeButton, BeButtonEvent, GraphicType} from "@itwin/core-frontend";
import { ColorDef, GraphicParams, RenderMaterial} from "@itwin/core-common";
import { PolyfaceBuilder, StrokeOptions } from "@itwin/core-geometry";
import { PopupToolbarManager, PopupToolbarProvider } from "./ToolbarPopup";

import { cView } from "./View";
import { Id64 } from "@itwin/core-bentley";
import { GltfDecorator } from "./GltfDecorator";
import { useEffect } from "react";

export class MarkerData {
  public id: string;
  public type: string;
  public point?: Point3d;
  public title: string;
  public description: string;
  public label: string | undefined;
  public image: HTMLImageElement | undefined;
  public material: RenderMaterial | undefined;
  public data: any | undefined;

  constructor() {
    this.id = "";
    this.type = "";
    this.title = "";
    this.description = "";
  }
}

//!!OA import { PopupMenu, PopupMenuEntry } from "./PopupMenu";
export class cMarker {
  public static _markerSets: Map<string, cMarkerSet>;

  private static iModel: IModelConnection;
  private static sViewPort: ScreenViewport;

  public static initMarkerPin = async (vp: ScreenViewport, imodel: IModelConnection) => {
    this.iModel = imodel;
    this.sViewPort = vp;

    this._markerSets = new Map();
  }

  public static decoratorIsSetup = () => {
    return null != this._markerSets;
  }

  public static getIModelConnection = () => {
    return this.iModel;
  }

  public static createMarkerSet = (markerSetId: string) => {
    if (!this.getMarkerSet(markerSetId)) {
      this._markerSets?.set(markerSetId, new cMarkerSet(markerSetId));
      this.setDrawFunction(markerSetId, 0);

//      this.enableMarkerSet(markerSetId);
    }
  }

  public static setDrawFunction = (markerSetId: string, drawFunc?: any) => {
    const markerSet = this.getMarkerSet(markerSetId);
    if (markerSet) markerSet.setDrawFunction(drawFunc);
  }

  public static setClickCallback = (markerSetId: string, clickCallback?: any) => {
    const markerSet = this.getMarkerSet(markerSetId);
    if (markerSet) markerSet.setClickCallback(clickCallback);
  }

  public static setToolbar = (markerSetId: string, toolbar: PopupToolbarProvider) => {
    const markerSet = this.getMarkerSet(markerSetId);
    if (markerSet) markerSet.setToolbar(toolbar);
  }

  public static addMarkerPoint = (markerSetId: string, markerData: MarkerData, drawFunc?: any, clickCallback?: any) => {
    const markerSet = this.getMarkerSet(markerSetId);
    if (markerSet) 
      markerSet.addMarker(markerData, drawFunc, clickCallback);
  }

  public static removeMarker = (markerSetId: string, markerId: string) => {
    const markerSet = this.getMarkerSet(markerSetId);
    if (markerSet) markerSet.removeMarker(markerId);
  }

  public static removeAllMarkers = (markerSetId: string) => {
    const markerSet = this.getMarkerSet(markerSetId);
    if (markerSet) markerSet.removeAllMarkers();
  }

  public static enableMarkerSet = (markerSetId: string) => {
    const markerSet = this.getMarkerSet(markerSetId);
    if (markerSet) IModelApp.viewManager.addDecorator(markerSet._markerDecorator);
  }

  public static disableMarkerSet = (markerSetId: string) => {
    const markerSet = this.getMarkerSet(markerSetId);
    if (markerSet) IModelApp.viewManager.dropDecorator(markerSet._markerDecorator);
  }

  public static enableAllMarketSet = () => {

  }

  public static setViewport = (vp?: ScreenViewport, markerSetId?: string) => {
    // const curVp = vp ?? cView.getCurrentView();
    // for (const [id, markSet] of this._markerSets!) {
      // if (markerSetId == null || markerSetId == id) {
        // markSet.setViewport(curVp);
      // }
    // }
  }

  private static getMarkerSet = (markerSetId: string) : cMarkerSet | undefined => {
    return this._markerSets.get(markerSetId);
  }
}

//const tb = new cIssuesToolbar();

/**
 * There are four classes that cooperate to produce the markers.
 *
 * cPinMarker  - Displays a single pin image at a given world location.  The image is offset upwards so that the
 *               bottom tip of the pin 'points' at the location.  This class also includes the logic for interacting
 *               with the marker.  This sample creates a cPinMarker for every location that is not clustered.
 *
 * cClusterMarker - Displays a circle representing a group of overlapping markers.  As the view is zoomed out, this
 *                  clustering avoids drawing large numbers of markers at the same location.  This sample creates a
 *                  cClusterMarker for every group of five or more locations that are close together on screen.
 *
 * cMarkerSet - Represents a logical collection of markers.  This sample creates two cMarkerSets.  One represents
 *              a "auto" pattern of locations (random, circle, etc.).  The other represents the locations created by
 *              the PlaceMarkerTool.
 *
 * cMarkerDecorator - The object which is registered with the ViewManager and is responsible for displaying all the
 *                       markers and marker clusters.  This sample creates exactly one cMarkerDecorator.
 */

/*********************************************************************************/
/* cPinMarker - Shows a pin marking the location of a point.                     */
/*********************************************************************************/

export class cPinMarker extends Marker {
  //!!OA  private _markerSet: cMarkerSet;
  public _id: string;
  public toolTipTitle: string;
  public toolTipDescription: string;
  private static _width = 15;
  private static _height = 30;
  private material: RenderMaterial;
  private parent: cMarkerSet;

  private _graphic: RenderGraphic | undefined;

  /** Create a new cPinMarker */
  constructor(markerData: MarkerData, markerSet: cMarkerSet) {
    const point = markerData.point!;

    // Use the same height for all the markers, but preserve the aspect ratio from the image
    //    super(point, new Point2d(0.1, 0.1));
    if (markerData.image) {
      console.log("in if");
      super(
        point,
        new Point2d(
          (markerData.image!.width / markerData.image!.height) *
            cPinMarker._height,
          cPinMarker._height
        )
      );
      this.setImage(markerData.image);
    } else {
      super(point, new Point2d(cPinMarker._width, cPinMarker._height)); // using decoration graphics instead
    }

    this._id = markerData.id;
    //    this._drawFunction = 0;

    // Keep a pointer back to the marker set
    //!!OA    this._markerSet = markerSet;

    // Add an offset so that the pin 'points' at the location, rather than floating in the middle of it
    this.imageOffset = new Point3d(0, Math.floor(this.size.y * 0.5));

    // The JSON data is stored on the marker
    //    this.data = markerData.data;

    // The title/description will be shown as a tooltip when the user interacts with the marker
    //    let tooltip = "";
    this.toolTipTitle = "";
    this.toolTipDescription = "";

    if (markerData.title) {
      this.toolTipTitle = markerData.title;
      this.toolTipDescription = markerData.description
        ? markerData.description
        : "";
      let tooltip = markerData.title;
      if (markerData.description) {
        tooltip += `<br>${markerData.description}`;
      }

      const div = document.createElement("div");
      div.innerHTML = tooltip;
      this.title = div;
    }

    if (markerData.label) {
      const label = document.createElement("div");
      label.style.backgroundColor = "rgba(210, 210, 210, 0.9)";
      label.style.padding = "5px";
      label.style.borderRadius = "3px";
      label.innerHTML = markerData.label;
      this.htmlElement = label;
    }

    this.material = markerData.material!;

    // The scale factor adjusts the size of the image' so it appears larger when close to the camera eye point.
    // Make size 75% at back of frustum and 200% at front of frustum (if camera is on)
    this.setScaleFactor({ low: 0.75, high: 2.0 });

    this.parent = markerSet;
  }

  /** Determine whether the point is within this Marker */
  public pick(pt: XAndY): boolean {
    if (undefined === this.imageOffset) return super.pick(pt);

    // This method is overridden because we want the marker picked based on the apparent size of the image, which is offset and scaled.
    const pickRect = this.rect.clone();
    const offsetX =
      undefined === this._scaleFactor
        ? this.imageOffset.x
        : this.imageOffset.x * this._scaleFactor.x;
    const offsetY =
      undefined === this._scaleFactor
        ? this.imageOffset.y
        : this.imageOffset.y * this._scaleFactor.y;
    pickRect.top -= offsetY;
    pickRect.bottom -= offsetY;
    pickRect.left -= offsetX;
    pickRect.right -= offsetX;

    return pickRect.containsPoint(pt);
  }

  public onMouseEnter(ev: BeButtonEvent) {
    if (this.parent.toolbar) {
      PopupToolbarManager.showToolbar(
        this.parent.toolbar,
        this._id,
        this.rect.right,
        this.rect.top
      );
    }

    this._isHilited = true;
    return true;
  }

  public onMouseLeave() {
    PopupToolbarManager.closeToolbar();
    this._isHilited = false;
  }

  /** This method will be called when the user clicks on a marker */
  public onMouseButton(ev: BeButtonEvent): boolean {
    if (
      BeButton.Data !== ev.button ||
      !ev.isDown ||
      !ev.viewport ||
      !ev.viewport.view.isSpatialView()
    )
      return true;

    if (this.parent.onMouseButtonCallback) {
      this.parent.onMouseButtonCallback(this._id);
      return true;
    }

    return true; // Don't allow clicks to be sent to active tool
  }

  public async getGraphic(fileName: string, iModel: IModelConnection) {
    if (!this._graphic) {
      const url = `/${fileName}.glb`;
      const file = await fetch(url);
      const buffer = await file.arrayBuffer();
      const id = iModel.transientIds.next;
  
      // Convert the glTF into a RenderGraphic.
      this._graphic = await readGltfGraphics({
        gltf: new Uint8Array(buffer),
        iModel,
        pickableOptions: { 
          id,
          modelId: Id64.invalid
         },
      });
    }
    return this._graphic;
  }

  public async displayGltfAsset(point: Point3d, iModel: IModelConnection): Promise<boolean> {
    try {
      // const [handle] = await (window as any).showOpenFilePicker({
      //   types: [
      //     {
      //       description: "glTF",
      //       accept: { "model/*": [".gltf", ".glb"] },
      //     },
      //   ],
      // });
      
      // const file = await handle.getFile();
      const fileName = "model";
      // const url = `/${fileName}.glb`;
      // const file = await fetch(url);
      // const buffer = await file.arrayBuffer();
  
      // // Allocate a new transient Id to identify the graphic so it can be picked.
      // const id = iModel.transientIds.next;
  
      // Convert the glTF into a RenderGraphic.
      let graphic = await this.getGraphic(fileName, iModel)
  
      if (!graphic)
        return false;

    const transform = Transform.createTranslation(point);
    const branch = new GraphicBranch();
    branch.add(graphic);
    graphic = IModelApp.renderSystem.createGraphicBranch(branch, transform);

    // Take ownership of the graphic so that it is not disposed of until we're finished with it.
    // By doing so we take responsibility for disposing of it ourselves.
    const owner = IModelApp.renderSystem.createGraphicOwner(graphic);

    // Install the decorator, using the file name as the tooltip.
    const information = "Made by Hanan";
    const id = iModel.transientIds.next;
    const decorator = new GltfDecorator(owner, fileName, id);
    const decorators = IModelApp.viewManager.decorators;
    // for (const object of decorators) {
    //   if(object instanceof GltfDecorator && object.getTooltip() === decorator.getTooltip()) {
    //     return true;
    //   }
    // }
    IModelApp.viewManager.addDecorator(decorator);

    // When the iModel is closed, dispose of the graphic and uninstall the decorator.
    iModel.onClose.addOnce(() => {
      owner.disposeGraphic();
      IModelApp.viewManager.dropDecorator(decorator);
    });

    return true;
  } catch (_) {
    return false;
  }
      

  }
  // called by Decorator
  public async addMarker(context: DecorateContext) {
    super.addMarker(context);
    //    console.log("cPinMarker::addMarker");
    if (this.parent.drawFunction === 0) return;


    const renderMaterial = this.material;
    if (renderMaterial) {
      const builder = context.createGraphicBuilder(GraphicType.WorldDecoration);
      //    const builder = context.createSceneGraphicBuilder();

      const angle = context.viewport.rotation.transpose();
      angle.multiplyMatrixMatrix(Matrix3d.create90DegreeRotationAroundAxis(0));

      // const vp = IModelApp.viewManager.selectedView;
      const vp = cView.getCurrentView()
      let eyePoint;
      if (vp && vp.view.is3d()) {
        eyePoint = vp.view.getEyePoint();
      }
      // This will offset the icon towards the camera to reduce the visual impact of the icon clipping with other elements
      if (eyePoint) {
        //        const point = this.worldLocation.plusScaled(vector, 0.3);
        const vector = this.worldLocation.unitVectorTo(eyePoint)!;
        const point = this.worldLocation.clone();
        point.plusXYZ(0, 0, 0.5, point);
        point.plusScaled(vector, 0.3, point);
        
        // !!! AH
        // For Displaying GLTF
        // const iModel = cMarker.getIModelConnection();
        
        // this.displayGltfAsset(point, iModel).then((status) => {
        //   console.log(status);
        //   context.addDecoration(GraphicType.WorldDecoration, builder.finish());
        // }).catch((err) => {
        //   console.error("Error:", err);
        // });

      

    

        const zoomFac = cView.zoomFactor();
        let size = 2.0 / zoomFac;
        if (size < 0.8) size = 0.8;
        let decorationEllipse = Arc3d.createScaledXYColumns(
          point,
          angle.multiplyMatrixMatrix(Matrix3d.create90DegreeRotationAroundAxis(2)),
          size,
          size
        );

        const graphicParams = new GraphicParams();
        graphicParams.material = renderMaterial;
        builder.activateGraphicParams(graphicParams);
        builder.addArc(decorationEllipse, true, true);
        //!!OA ***
        const options = StrokeOptions.createForCurves();
        options.needParams = true;
        options.needNormals = true;
        const pFaceBuilder = PolyfaceBuilder.create(options);

        // draw sphere
        const shape = Sphere.createCenterRadius(new Point3d(0, 0, 0), 2);
        //        pFaceBuilder.addSphere(shape);

        const p1 = Point3d.create(0, 0, 0);
        const p2 = Point3d.create(0, 0, 5);
        const p3 = Point3d.create(0, 5, 5);
        const p4 = Point3d.create(0, 5, 0);
        const rect = [p1, p2, p3, p4];
        pFaceBuilder.addQuadFacet(rect);

        const polyface = pFaceBuilder.claimPolyface(false);
        const transform = Transform.createTranslation(this.worldLocation);
        polyface.tryTransformInPlace(transform);
        //      builder.addPolyface(polyface, true);
        //!!OA ***

        context.addDecoration(GraphicType.WorldDecoration, builder.finish());
      }
    } else {
      const builder = context.createGraphicBuilder(GraphicType.WorldDecoration);
      builder.setSymbology(ColorDef.white, ColorDef.red, 2); // default color

      const polyface = this.parent.drawFunction(builder, this._id);

      const transform = Transform.createTranslation(this.worldLocation);
      polyface.tryTransformInPlace(transform);

      builder.addPolyface(polyface, true);
      context.viewport.viewFlags = context.viewFlags.with("visibleEdges", true);
      context.addDecoration(GraphicType.WorldDecoration, builder.finish());
    }

    /*  */




    //    context.addDecoration(GraphicType.Scene, builder.finish());
  }

  //   public drawFunc(ctx: CanvasRenderingContext2D) {
  //     ctx.beginPath();
  //     ctx.strokeStyle = "#372528";
  //     ctx.fillStyle = "white";
  //     ctx.lineWidth = 1;
  //     ctx.fillRect(-15, -12, 30, 15);
  //     // ctx.arc(0, 0, 13, 0, Math.PI * 2);
  //     // ctx.fill();
  // //    ctx.strokeText("DID", 2, 0);
  //     ctx.stroke();
  //   }
}

/*******************************************************************************/
/* cMarkerSet - A MarkerSet to hold pin instances.                             */
/* This class supplies to `getClusterMarker` method to create cClusterMarker.  */
/*******************************************************************************/
class cMarkerSet extends MarkerSet<cPinMarker> {
  private _id: string;
  public minimumClusterSize = 500; //!!OA changed from 5
  public onMouseButtonCallback?: any;
  public drawFunction?: any;
  public toolbar?: PopupToolbarProvider;
  public _markerDecorator: cMarkerDecorator;

  constructor(markerSetId: string) {
    super();
    this._id = markerSetId;
    this.drawFunction = 0;
    this._markerDecorator = new cMarkerDecorator(this);
  }

  public setDrawFunction = (drawFunction: any) => {
    this.drawFunction = drawFunction;
  }

  public setClickCallback = (clickCallback: any) => {
    this.onMouseButtonCallback = clickCallback;
  }

  public setToolbar = (toolbarProvider: PopupToolbarProvider) => {
    this.toolbar = toolbarProvider;
  }

  public setViewport = (vp: ScreenViewport) => {
    this.changeViewport(vp);
  }

  // This method is called from within the MarkerSet base class based on the proximity of the markers and the minimumClusterSize
  protected getClusterMarker = (cluster: Cluster<cPinMarker>): Marker => {
    return cClusterMarker.makeFrom(
      cluster.markers[0],
      cluster,
      this.onMouseButtonCallback
    );
  }

  /* Adds a single new marker to the markerset */
  public addMarker = (markerData: MarkerData, drawFunc?: any, clickCallback?: any): void => {
    const onClickCallback = clickCallback ?? this.onMouseButtonCallback;
    const drawFunction = drawFunc ?? this.drawFunction;

    // const pinMarker = new cPinMarker(markerData, this);
    // pinMarker.setDrawFunction(drawFunction);
    // pinMarker.setClickCallback(onClickCallback);
    this.markers.add(new cPinMarker(markerData, this));

    this.updateView();
  }

  /** Drop all markers from the set. */
  public removeAllMarkers = () => {
    this.markers.clear();
    this.updateView();
  }

  /** Drop one particular marker from the set. */
  public removeMarker = (markerId: string) => {
    for (const pin of this.markers) {
      if (pin._id === markerId) {
        this.markers.delete(pin);
        break;
      }
    }
    this.updateView();
  }

  public updateView = () => {
    // When the markers change we notify the viewmanager to remove the existing decorations
    // const vp = IModelApp.viewManager.selectedView;
    const vp = cView.getCurrentView()

    if (undefined !== vp) vp.invalidateDecorations();
  }
}

/*******************************************************************************/
/* cClusterMarker - Marker to show as a cluster of overlapping markers.        */
/*******************************************************************************/
class cClusterMarker extends Marker {
  private static _radius = 13;
  private _cluster: any;
  private _onMouseButtonCallback?: any;

  /** Create a new cluster marker */
  constructor(location: XYAndZ, size: XAndY, cluster: Cluster<cPinMarker>, onMouseButtonCallback?: any) {
    super(location, size);

    this._onMouseButtonCallback = onMouseButtonCallback;
    this._cluster = cluster;

    // The cluster will be drawn as a circle
    // Display the count of markers in this cluster
    this.label = cluster.markers.length.toLocaleString();
    this.labelColor = "black";
    this.labelFont = "bold 14px san-serif";

    // Concatenate the tooltips from the markers to create the tooltip for the cluster
    const maxLen = 10;
    let title = "";
    cluster.markers.forEach((marker, index: number) => {
      if (index < maxLen) {
        if (index === 0) title += marker.toolTipTitle;
        if (marker.toolTipDescription === undefined || marker.toolTipDescription.length === 0)
          title += `<br>${marker.toolTipTitle}`;
        else 
          title += `<br>${marker.toolTipDescription}`;
      }
    });

    if (cluster.markers.length > maxLen) title += "<br>...";

    const div = document.createElement("div");
    div.innerHTML = title;
    this.title = div;
  }

  /** This method will be called when the user clicks on a marker */
  public onMouseButton = (ev: BeButtonEvent): boolean => {
    if (
      BeButton.Data !== ev.button ||
      !ev.isDown ||
      !ev.viewport ||
      !ev.viewport.view.isSpatialView()
    )
      return true;

    if (this._onMouseButtonCallback)
      this._onMouseButtonCallback(this._cluster.markers[0].data);

    return true; // Don't allow clicks to be sent to active tool
  }

  /** Show the cluster as a white circle with a thick outline */
  public drawFunc = (ctx: CanvasRenderingContext2D) => {
    ctx.beginPath();
    ctx.strokeStyle = "#372528";
    ctx.fillStyle = "white";
    ctx.lineWidth = 5;
    ctx.arc(0, 0, cClusterMarker._radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
  }
}

/************************************************************************************/
/* cMarkerDecorator - This class can be registered with ViewManager.addDecorator    */
/* Once registered, the decorate method will be called with a supplied              */
/* DecorateContext.  The Decorator will call the MarkerSet to create decorations    */
/************************************************************************************/
export class cMarkerDecorator implements Decorator {
  private _markerSet: cMarkerSet; // each markerset has one markerSet only

  constructor(markerSet : cMarkerSet) {
    this._markerSet = markerSet;
  }

  /* Implement this method to add Decorations into the supplied DecorateContext. */
  public decorate = (context: DecorateContext): void => {
    /* This method is called for every rendering frame.  We will reuse our marker sets since the locations and images
       for the markers don't typically change. */
    if (context.viewport.view.isSpatialView()) {
      this._markerSet.addDecoration(context);
    }
  }
}
